import { Stack, Typography, useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import { IconButton } from 'design-system';
import { MdArrowBack, MdClose } from 'react-icons/md';
import { useFeatures } from 'hooks/useFeatures';
import { DescriptionVariant, Field, FormFragment as FormData, FormType, InputType } from 'generated/graphql';
import ActionButton from './ActionButton';
import FormField from './Form/FormField';
import FormGroups from './Form/FormGroups';
import FormSets from './Form/FormSets';

const Form = ({
  hasWhiteFields = false,
  form,
  hideTitle = false,
  isLoading = false,
  isFormDialog = false,
  onBack = undefined,
  onClose = undefined,
  onFieldUpdateNew = async () => {},
  onRefresh,
  onFormRefresh,
}: {
  hasWhiteFields?: boolean;
  hideTitle?: boolean;
  form?: FormData;
  isLoading?: boolean;
  isFormDialog?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  onFieldUpdateNew: (
    fieldGroup: Field[],
    field: Field,
    value: string,
    values?: string[],
    entityID?: string,
  ) => Promise<void>;
  onRefresh?: () => void;
  onFormRefresh?: () => void;
}) => {
  const showInlineHeaderActions = isFormDialog && !hideTitle;

  const theme = useTheme();
  const { isEnabled } = useFeatures();

  if (!form) {
    return null;
  }

  const isQCStorage = form?.type === FormType.SetQualityReport;
  const isEvenCallout = isEnabled('QuotesSearch') && form?.type === FormType.SetQuote;

  const hasGroups = Boolean(form.fieldGroups?.length);
  const hasSets = Boolean(form.fieldSets?.length);

  const firstField = form.fields.findIndex((field) => field.type !== InputType.Hidden);
  const firstGroup = form.fieldGroups.findIndex((fieldGroup) =>
    fieldGroup.fields.some((field) => field.type !== InputType.Hidden),
  );
  const firstSet = form.fieldSets.findIndex((set) =>
    set.groups.some((group) => group.fields.some((field) => field.type !== InputType.Hidden)),
  );
  return (
    <>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {!hideTitle && (
          <>
            {onBack && (
              <IconButton
                aria-label="Back"
                onClick={onBack}
                sx={{ ml: -1, mr: 0.5 }}
                track={false}
                size="small"
                color="primary"
              >
                <MdArrowBack size={20} />
              </IconButton>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                mr: 0.5,
              }}
            >
              <Typography variant="h6" sx={(theme) => ({ color: theme.palette.primary.main })}>
                {form.title}
              </Typography>

              {showInlineHeaderActions && form.actionButtons.length > 0 && (
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  {form.actionButtons.map((button, index) => (
                    <ActionButton key={index} {...button} onClose={onClose} onMutate={onRefresh} />
                  ))}
                </Stack>
              )}
            </Stack>

            {onClose && (
              <IconButton onClick={onClose} sx={{ mr: -1, pr: -1 }} track={false}>
                <MdClose size={24} fill={theme.palette.grey[700]} />
              </IconButton>
            )}
          </>
        )}
      </Stack>
      {form.description.length > 0 ? (
        form.descriptionVariant === DescriptionVariant.Callout ? (
          <Alert severity="success" icon={false} sx={isEvenCallout ? { mb: 2 } : undefined}>
            <Typography variant="body1" sx={{ marginBottom: isEvenCallout ? 0 : 2 }}>
              {form.description}
            </Typography>
          </Alert>
        ) : (
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.grey[700],
              mb: theme.spacing(2),
              fontSize: '0.875rem',
              maxWidth: onClose ? 'calc(100% - 24px)' : '100%',
            })}
            dangerouslySetInnerHTML={{ __html: form.description }}
          />
        )
      ) : null}
      {!showInlineHeaderActions && form.actionButtons.length > 0 && (
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {form.actionButtons.map((button, index) => (
            <ActionButton key={index} {...button} onClose={onClose} onMutate={onRefresh} />
          ))}
        </Stack>
      )}
      {hasSets ? (
        <FormSets
          hasWhiteFields={hasWhiteFields}
          fieldSets={form.fieldSets}
          isLoading={isLoading}
          onFieldUpdateNew={onFieldUpdateNew}
          onRefresh={onRefresh}
          onFormRefresh={onFormRefresh}
          isFormDialog={isFormDialog}
          autoFocusSet={firstSet}
          isQCStorage={isQCStorage}
          formType={form.type}
          onClose={onClose}
        />
      ) : hasGroups ? (
        <FormGroups
          hasWhiteFields={hasWhiteFields}
          fieldGroups={form.fieldGroups}
          isLoading={isLoading}
          onFieldUpdateNew={onFieldUpdateNew}
          onRefresh={onRefresh}
          onFormRefresh={onFormRefresh}
          isFormDialog={isFormDialog}
          autoFocusGroup={firstGroup}
          isQCStorage={isQCStorage}
          formType={form.type}
          onClose={onClose}
        />
      ) : (
        form.fields.map((field, index, fields) => (
          <FormField
            key={field.id}
            hasWhiteFields={hasWhiteFields}
            field={field}
            autoFocus={index === firstField}
            allFields={fields}
            disabled={isLoading}
            onChange={onFieldUpdateNew}
            isQCStorage={isQCStorage}
            formType={form.type}
          />
        ))
      )}
    </>
  );
};

export default Form;
